import React from "react";
import {
  AbsoluteFill,
  Easing,
  interpolate,
  Sequence,
  spring,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";

const C = {
  black: "#070707",
  ivory: "#F3F0E8",
  acid: "#D8FF3E",
  cobalt: "#3155FF",
  coral: "#FF4F38",
};

const clamp = {
  extrapolateLeft: "clamp" as const,
  extrapolateRight: "clamp" as const,
};

const smooth = Easing.bezier(0.16, 1, 0.3, 1);
const editorial = Easing.bezier(0.45, 0, 0.55, 1);
const accelerate = Easing.bezier(0.7, 0, 0.84, 0);

const progress = (
  frame: number,
  from: number,
  to: number,
  easing: (value: number) => number = smooth,
) =>
  interpolate(frame, [from, to], [0, 1], {
    ...clamp,
    easing,
  });

const fadeWindow = (
  frame: number,
  fadeIn: [number, number],
  fadeOut: [number, number],
) =>
  interpolate(frame, [...fadeIn, ...fadeOut], [0, 1, 1, 0], {
    ...clamp,
    easing: editorial,
  });

const Grain: React.FC = () => {
  const frame = useCurrentFrame();

  return (
    <svg
      viewBox="0 0 1920 1080"
      aria-hidden="true"
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        opacity: 0.075,
        pointerEvents: "none",
        mixBlendMode: "soft-light",
      }}
    >
      <filter id="premium-noise">
        <feTurbulence
          type="fractalNoise"
          baseFrequency="0.85"
          numOctaves="3"
          seed={(frame % 5) + 1}
          stitchTiles="stitch"
        />
      </filter>
      <rect width="1920" height="1080" filter="url(#premium-noise)" />
    </svg>
  );
};

const TechnicalFrame: React.FC<{
  chapter: string;
  light?: boolean;
}> = ({chapter, light = false}) => {
  const frame = useCurrentFrame();
  const color = light ? C.black : C.ivory;

  return (
    <div className="pm-tech" style={{color}}>
      <span className="pm-tech-tl">MOTION / DIRECTION</span>
      <span className="pm-tech-tr">{chapter}</span>
      <span className="pm-tech-bl">EASE / VELOCITY / FORM</span>
      <span className="pm-tech-br">{String(frame).padStart(3, "0")}F</span>
    </div>
  );
};

const ChromaticWord: React.FC<{
  children: React.ReactNode;
  amount: number;
}> = ({children, amount}) => {
  return (
    <span className="pm-chromatic">
      <span className="pm-chromatic-base">{children}</span>
      <span
        className="pm-chromatic-color pm-chromatic-blue"
        style={{transform: `translateX(${-amount}px)`}}
      >
        {children}
      </span>
      <span
        className="pm-chromatic-color pm-chromatic-red"
        style={{transform: `translateX(${amount}px)`}}
      >
        {children}
      </span>
    </span>
  );
};

const PrecisionScene: React.FC = () => {
  const frame = useCurrentFrame();
  const reveal = progress(frame, 4, 38);
  const settle = spring({
    frame,
    fps: 30,
    config: {damping: 22, stiffness: 95, mass: 1.1},
  });
  const chroma = interpolate(frame, [4, 36, 64], [42, 14, 0], {
    ...clamp,
    easing: smooth,
  });
  const exit = progress(frame, 76, 105, accelerate);
  const cameraScale = interpolate(frame, [0, 70, 105], [1.08, 1, 1.42], {
    ...clamp,
    easing: editorial,
  });
  const cameraY = interpolate(frame, [0, 72, 105], [50, 0, -110], {
    ...clamp,
    easing: editorial,
  });
  const line = progress(frame, 20, 62);

  return (
    <AbsoluteFill
      style={{
        backgroundColor: C.black,
        color: C.ivory,
        opacity: 1 - exit,
        overflow: "hidden",
      }}
    >
      <TechnicalFrame chapter="01 / PRECISION" />
      <div
        className="pm-camera pm-precision-camera"
        style={{
          transform: `translate3d(0, ${cameraY}px, 0) scale(${cameraScale})`,
        }}
      >
        <div
          className="pm-precision-kicker"
          style={{
            opacity: reveal,
            transform: `translateY(${(1 - reveal) * 30}px)`,
          }}
        >
          Designer motion system / 2026
        </div>
        <h1
          className="pm-precision-title"
          style={{
            clipPath: `inset(${(1 - reveal) * 100}% 0 0 0)`,
            transform: `translateY(${(1 - settle) * 120}px)`,
          }}
        >
          <ChromaticWord amount={chroma}>PRECISION</ChromaticWord>
          <span className="pm-precision-italic">in motion.</span>
        </h1>
        <div className="pm-rule">
          <div style={{transform: `scaleX(${line})`}} />
        </div>
      </div>
      <div
        className="pm-color-orb pm-orb-blue"
        style={{
          opacity: reveal * 0.9,
          transform: `translate3d(${interpolate(settle, [0, 1], [-260, 80])}px, ${interpolate(settle, [0, 1], [140, 0])}px, 0) scale(${0.65 + settle * 0.35})`,
        }}
      />
      <div
        className="pm-color-orb pm-orb-red"
        style={{
          opacity: reveal * 0.9,
          transform: `translate3d(${interpolate(settle, [0, 1], [250, -40])}px, ${interpolate(settle, [0, 1], [-100, 20])}px, 0) scale(${0.65 + settle * 0.35})`,
        }}
      />
      <Grain />
    </AbsoluteFill>
  );
};

const ApertureIcon: React.FC<{rotation: number; spread: number}> = ({
  rotation,
  spread,
}) => {
  const blades = Array.from({length: 8});

  return (
    <svg
      viewBox="-320 -320 640 640"
      className="pm-aperture"
      aria-hidden="true"
    >
      <circle cx="0" cy="0" r="285" fill="none" stroke={C.black} strokeWidth="2" />
      <circle cx="0" cy="0" r="214" fill="none" stroke={C.black} strokeWidth="1" opacity="0.35" />
      <g transform={`rotate(${rotation})`}>
        {blades.map((_, index) => (
          <path
            key={index}
            d={`M 0 ${-52 - spread} C 74 ${-176 - spread}, 188 -142, 232 -42 L 84 48 Z`}
            fill={index % 2 === 0 ? C.cobalt : C.coral}
            opacity="0.9"
            style={{mixBlendMode: "multiply"}}
            transform={`rotate(${index * 45})`}
          />
        ))}
      </g>
      <circle cx="0" cy="0" r={50 + spread * 0.18} fill={C.ivory} stroke={C.black} strokeWidth="2" />
    </svg>
  );
};

const ApertureScene: React.FC = () => {
  const frame = useCurrentFrame();
  const reveal = progress(frame, 0, 26);
  const hold = progress(frame, 18, 62, editorial);
  const speedRamp = progress(frame, 65, 116, Easing.bezier(0.8, 0, 0.2, 1));
  const fade = fadeWindow(frame, [0, 18], [104, 126]);
  const rotation = interpolate(frame, [0, 65, 126], [-32, 12, 245], {
    ...clamp,
    easing: editorial,
  });
  const scale = interpolate(speedRamp, [0, 1], [0.82, 4.9]);
  const x = interpolate(speedRamp, [0, 1], [0, -500]);
  const spread = interpolate(hold, [0, 1], [92, 0]);

  return (
    <AbsoluteFill
      style={{
        color: C.black,
        backgroundColor: C.ivory,
        opacity: fade,
        overflow: "hidden",
      }}
    >
      <TechnicalFrame chapter="02 / FOCUS" light />
      <div
        className="pm-camera pm-aperture-camera"
        style={{
          transform: `translate3d(${x}px, 0, 0) scale(${scale})`,
        }}
      >
        <ApertureIcon rotation={rotation} spread={spread} />
      </div>
      <div
        className="pm-focus-copy"
        style={{
          opacity: reveal * (1 - speedRamp),
          transform: `translate3d(${interpolate(reveal, [0, 1], [100, 0])}px, 0, 0)`,
        }}
      >
        <span>Camera as choreography</span>
        <h2>
          Focus the eye.
          <br />
          <em>Then move it.</em>
        </h2>
      </div>
      <Grain />
    </AbsoluteFill>
  );
};

const Ribbon: React.FC<{
  text: string;
  color: string;
  y: number;
  rotation: number;
  direction: 1 | -1;
  frame: number;
  delay: number;
}> = ({text, color, y, rotation, direction, frame, delay}) => {
  const enter = progress(frame, delay, delay + 30);
  const travel = progress(frame, delay + 16, 126, editorial);
  const x = interpolate(travel, [0, 1], [direction * 440, direction * -400]);

  return (
    <div
      className="pm-ribbon"
      style={{
        top: y,
        color: C.black,
        backgroundColor: color,
        opacity: enter,
        transform: `translate3d(${x}px, 0, 0) rotate(${rotation}deg) scaleY(${enter})`,
      }}
    >
      <span>{text}</span>
      <span>{text}</span>
      <span>{text}</span>
    </div>
  );
};

const FlowScene: React.FC = () => {
  const frame = useCurrentFrame();
  const intro = progress(frame, 0, 26);
  const ramp = progress(frame, 74, 132, Easing.bezier(0.85, 0, 0.15, 1));
  const opacity = fadeWindow(frame, [0, 14], [118, 140]);
  const cameraX = interpolate(ramp, [0, 1], [0, -480]);
  const cameraY = interpolate(ramp, [0, 1], [0, 160]);
  const cameraScale = interpolate(ramp, [0, 1], [1, 1.48]);
  const cameraRotation = interpolate(ramp, [0, 1], [0, -5]);

  return (
    <AbsoluteFill
      style={{
        color: C.ivory,
        backgroundColor: C.black,
        opacity,
        overflow: "hidden",
      }}
    >
      <TechnicalFrame chapter="03 / FLOW" />
      <div
        className="pm-camera"
        style={{
          transform: `translate3d(${cameraX}px, ${cameraY}px, 0) scale(${cameraScale}) rotate(${cameraRotation}deg)`,
        }}
      >
        <Ribbon text="EASY EASE • " color={C.acid} y={165} rotation={-5} direction={1} frame={frame} delay={2} />
        <Ribbon text="SPEED RAMP • " color={C.coral} y={435} rotation={3} direction={-1} frame={frame} delay={10} />
        <Ribbon text="FOLLOW THROUGH • " color={C.cobalt} y={705} rotation={-3} direction={1} frame={frame} delay={18} />
      </div>
      <div
        className="pm-flow-caption"
        style={{
          opacity: intro * (1 - ramp),
          transform: `translateY(${interpolate(intro, [0, 1], [40, 0])}px)`,
        }}
      >
        Motion has rhythm.
      </div>
      <Grain />
    </AbsoluteFill>
  );
};

const BezierIcon: React.FC<{progressValue: number}> = ({progressValue}) => {
  const pathLength = 710;

  return (
    <svg
      viewBox="0 0 760 420"
      className="pm-bezier"
      aria-hidden="true"
    >
      <line x1="80" y1="340" x2="80" y2="80" stroke={C.ivory} strokeWidth="2" opacity="0.22" />
      <line x1="80" y1="340" x2="680" y2="340" stroke={C.ivory} strokeWidth="2" opacity="0.22" />
      <line x1="80" y1="340" x2="260" y2="70" stroke={C.ivory} strokeWidth="2" opacity="0.42" />
      <line x1="680" y1="80" x2="500" y2="350" stroke={C.ivory} strokeWidth="2" opacity="0.42" />
      <circle cx="260" cy="70" r="11" fill={C.coral} />
      <circle cx="500" cy="350" r="11" fill={C.cobalt} />
      <path
        d="M 80 340 C 260 70, 500 350, 680 80"
        fill="none"
        stroke={C.acid}
        strokeWidth="12"
        strokeLinecap="round"
        strokeDasharray={pathLength}
        strokeDashoffset={pathLength * (1 - progressValue)}
      />
    </svg>
  );
};

const FinaleScene: React.FC = () => {
  const frame = useCurrentFrame();
  const reveal = progress(frame, 0, 38);
  const draw = progress(frame, 12, 76, editorial);
  const settle = spring({
    frame: frame - 12,
    fps: 30,
    config: {damping: 24, stiffness: 105, mass: 1},
  });
  const endFade = interpolate(frame, [98, 120], [1, 0], {
    ...clamp,
    easing: Easing.in(Easing.cubic),
  });
  const cameraScale = interpolate(frame, [0, 95, 120], [1.12, 1, 0.94], {
    ...clamp,
    easing: editorial,
  });

  return (
    <AbsoluteFill
      style={{
        color: C.ivory,
        backgroundColor: C.black,
        opacity: endFade,
        overflow: "hidden",
      }}
    >
      <TechnicalFrame chapter="04 / EASE" />
      <div
        className="pm-camera pm-final-camera"
        style={{transform: `scale(${cameraScale})`}}
      >
        <div
          className="pm-final-copy"
          style={{
            opacity: reveal,
            transform: `translate3d(${(1 - settle) * -90}px, 0, 0)`,
          }}
        >
          <span>Designer motion / deterministic code</span>
          <h2>
            Smooth is
            <br />
            <em>intentional.</em>
          </h2>
        </div>
        <div
          className="pm-bezier-wrap"
          style={{
            opacity: reveal,
            transform: `translate3d(${(1 - settle) * 110}px, 0, 0)`,
          }}
        >
          <BezierIcon progressValue={draw} />
          <div className="pm-curve-label">
            CUBIC-BEZIER
            <span>0.16 / 1 / 0.30 / 1</span>
          </div>
        </div>
      </div>
      <div
        className="pm-final-flood"
        style={{
          transform: `scaleX(${progress(frame, 80, 112, accelerate)})`,
        }}
      />
      <Grain />
    </AbsoluteFill>
  );
};

export const PremiumMotion: React.FC = () => {
  const {fps} = useVideoConfig();

  return (
    <AbsoluteFill>
      <Sequence durationInFrames={105} premountFor={fps}>
        <PrecisionScene />
      </Sequence>
      <Sequence from={84} durationInFrames={126} premountFor={fps}>
        <ApertureScene />
      </Sequence>
      <Sequence from={190} durationInFrames={140} premountFor={fps}>
        <FlowScene />
      </Sequence>
      <Sequence from={315} durationInFrames={120} premountFor={fps}>
        <FinaleScene />
      </Sequence>
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
